import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus } from 'lucide-react';

const characters = {
  fairytale: [
    { id: 'princess', name: 'Princess Luna', emoji: '👸' },
    { id: 'dragon', name: 'Dragon Spark', emoji: '🐉' },
    { id: 'wizard', name: 'Wizard Merlin', emoji: '🧙' },
    { id: 'knight', name: 'Knight Alex', emoji: '⚔️' },
  ],
  realistic: [
    { id: 'mentor', name: 'Coach Sarah', emoji: '👩‍🏫' },
    { id: 'friend', name: 'Friend Jordan', emoji: '👦' },
    { id: 'inventor', name: 'Inventor Tim', emoji: '👨‍🔬' },
    { id: 'adventurer', name: 'Explorer Maya', emoji: '🧗' },
  ],
  mixed: [
    { id: 'cyborg', name: 'Cyborg Nova', emoji: '🤖' },
    { id: 'mystical', name: 'Spirit Guide', emoji: '✨' },
    { id: 'pirate', name: 'Space Pirate', emoji: '🏴‍☠️' },
    { id: 'magic_animal', name: 'Magic Fox', emoji: '🦊' },
  ],
};

export default function CharacterSelection() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'story';
  const [customName, setCustomName] = useState('');
  const [customEmoji, setCustomEmoji] = useState('⭐');

  const handleSelectCharacter = (character: { id: string; name: string; emoji: string }) => {
    localStorage.setItem('selectedCharacter', JSON.stringify(character));
    navigate(`/chat?mode=${mode}`);
  };

  const handleCustomCharacter = () => {
    if (!customName.trim()) return;
    const customChar = { id: 'custom', name: customName, emoji: customEmoji };
    localStorage.setItem('selectedCharacter', JSON.stringify(customChar));
    navigate(`/chat?mode=${mode}`);
  };

  const CharacterCard = ({ char }: { char: { id: string; name: string; emoji: string } }) => (
    <Card
      className="p-6 text-center cursor-pointer hover:shadow-lg transition-all transform hover:scale-105 bg-white/95 backdrop-blur"
      onClick={() => handleSelectCharacter(char)}
    >
      <div className="text-5xl mb-3">{char.emoji}</div>
      <h3 className="font-bold text-gray-800">{char.name}</h3>
      <Button className="w-full mt-4 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white text-sm">
        Select
      </Button>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-300 via-purple-300 to-pink-300 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-2">Pick Your Character</h1>
          <p className="text-white/80">Choose a friend to chat with or create your own!</p>
        </div>

        <Tabs defaultValue="fairytale" className="mb-8">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="fairytale">Fairytale</TabsTrigger>
            <TabsTrigger value="realistic">Realistic</TabsTrigger>
            <TabsTrigger value="mixed">Mixed</TabsTrigger>
          </TabsList>

          <TabsContent value="fairytale">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {characters.fairytale.map((char) => (
                <CharacterCard key={char.id} char={char} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="realistic">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {characters.realistic.map((char) => (
                <CharacterCard key={char.id} char={char} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="mixed">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {characters.mixed.map((char) => (
                <CharacterCard key={char.id} char={char} />
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex gap-4">
          <Dialog>
            <DialogTrigger asChild>
              <Button className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white h-12">
                <Plus className="w-5 h-5 mr-2" />
                Create Your Own
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader>
                <DialogTitle>Create Your Character</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Character Name
                  </label>
                  <Input
                    placeholder="e.g., Brave Tiger"
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Pick an Emoji
                  </label>
                  <div className="text-5xl mb-4">{customEmoji}</div>
                  <Input
                    placeholder="Paste an emoji here"
                    value={customEmoji}
                    onChange={(e) => setCustomEmoji(e.target.value.slice(0, 2))}
                    maxLength={2}
                  />
                </div>
                <Button
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white"
                  onClick={handleCustomCharacter}
                  disabled={!customName.trim()}
                >
                  Create Character
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Button
            variant="outline"
            className="px-6"
            onClick={() => navigate('/modes')}
          >
            Back
          </Button>
        </div>
      </div>
    </div>
  );
}
